import { useEffect, useRef, useState } from 'react';

const reduceMotion = () =>
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const defaultFormat = (n: number) => n.toLocaleString('zh-TW');

/** 數字從上一個值滾到新值（首次由 0 起算），用 rAF 補間 640ms。
 *  prefers-reduced-motion 下直接顯示終值。
 *
 *  只用在指標與數量這種「看趨勢」的數字；會被程式讀取比對的結果數不要用。 */
export function CountUp({
  value,
  format = defaultFormat,
  duration = 640,
  className,
}: {
  value: number | null | undefined;
  format?: (n: number) => string;
  duration?: number;
  className?: string;
}) {
  const [reduce] = useState(reduceMotion);
  const initial = reduce ? Number(value ?? 0) : 0;
  const [shown, setShown] = useState<number>(initial);
  const cur = useRef<number>(initial);

  useEffect(() => {
    if (value == null || reduce) {
      const v = Number(value ?? 0);
      cur.current = v;
      setShown(v);
      return undefined;
    }
    const from = cur.current;
    const to = Number(value);
    if (from === to) return undefined;
    const t0 = performance.now();
    let raf = 0;
    const step = (t: number) => {
      const k = Math.min(1, (t - t0) / duration);
      const eased = 1 - (1 - k) ** 3;
      const v = Math.round(from + (to - from) * eased);
      cur.current = v;
      setShown(v);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration, reduce]);

  if (value == null) return <span className={className}>—</span>;
  return <span className={className}>{format(shown)}</span>;
}
